import type {
  AvatarResponse,
  CastingProfileResponse,
  UpdateCastingProfileRequest
} from '@innovationdepartment/proxima-sdk-axios';
import { AxiosError } from 'axios';
import useProximaSDK from 'hooks/useProximaSDK';
import { useBrandStore } from 'stores';

import type { CastingMetadata } from './avatarManagement.types';

// ─── Error formatting ────────────────────────────────────────────────────────

function formatSdkError(error: unknown): string {
  if (error instanceof AxiosError) {
    const status = error.response?.status ?? 'unknown';
    const data = error.response?.data;
    const bodyMessage =
      data != null &&
      typeof data === 'object' &&
      'message' in data &&
      String((data as { message: unknown }).message).trim();
    return bodyMessage
      ? `Request failed (${status}): ${bodyMessage}`
      : `Request failed (${status})`;
  }
  return error instanceof Error ? error.message : 'Request failed';
}

// ─── Result types ────────────────────────────────────────────────────────────

export type GetCastingProfileResult =
  | { data: CastingProfileResponse | null; error?: undefined }
  | { data?: undefined; error: string };

export type GetAvatarsResult =
  | { data: AvatarResponse[]; error?: undefined }
  | { data?: undefined; error: string };

export type GetMetadataResult =
  | { data: CastingMetadata; error?: undefined }
  | { data?: undefined; error: string };

export type MutationResult =
  | { success: true; error?: undefined }
  | { success?: undefined; error: string };

// ─── Hook ────────────────────────────────────────────────────────────────────

function useAvatarManagementApi() {
  const brandProfileApi = useProximaSDK('BrandProfileApi');
  const creativeEngineApi = useProximaSDK('CreativeEngineApi');
  const { brand } = useBrandStore();
  const { brandId } = brand;

  const getCastingProfile = async (): Promise<GetCastingProfileResult> => {
    if (!brandId) return { error: 'Missing brandId' };

    try {
      const response = await brandProfileApi.getCastingProfile({ brandId });
      // A null body means the brand has no casting profile yet — a valid state.
      return { data: response.data || null };
    } catch (e) {
      return { error: formatSdkError(e) };
    }
  };

  const updateCastingProfile = async (
    updateCastingProfileRequest: UpdateCastingProfileRequest
  ): Promise<MutationResult> => {
    if (!brandId) return { error: 'Missing brandId' };

    try {
      await brandProfileApi.updateCastingProfile({ brandId, updateCastingProfileRequest });
      return { success: true };
    } catch (e) {
      return { error: formatSdkError(e) };
    }
  };

  const getAvatarLibrary = async (): Promise<GetAvatarsResult> => {
    try {
      const response = await creativeEngineApi.getAllAvatars();
      const { data } = response;
      if (data == null) return { error: 'No data in response' };
      return { data: data.avatars };
    } catch (e) {
      return { error: formatSdkError(e) };
    }
  };

  const getMetadata = async (): Promise<GetMetadataResult> => {
    try {
      const response = await creativeEngineApi.getAvatarLibraryMetadata();
      return { data: response.data };
    } catch (e) {
      return { error: formatSdkError(e) };
    }
  };

  return {
    getCastingProfile,
    updateCastingProfile,
    getAvatarLibrary,
    getMetadata,
    loading: brandProfileApi.loading || creativeEngineApi.loading,
    brandId
  };
}

export default useAvatarManagementApi;
